import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatsAPI } from '../../services/api';
import { Conversation, ConversationComment, CompanyUser, Agent } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Download, Info, Mic, User as UserIcon, X, Square, CheckSquare, Calendar, ExternalLink, Send } from 'lucide-react';
import { serverIsoToMillis, formatServerDate, formatServerTime, formatServerDateTime } from '../../utils/datetime';
import AgentMessageContent from '../shared/AgentMessageContent';

interface VoiceTabProps {
    agents: Agent[];
    teamMembers: CompanyUser[];
    hasAssignPermission: boolean;
    mobilePanel: 'list' | 'chat' | 'details';
    selectedChat: Conversation | null;
    setSelectedChat: React.Dispatch<React.SetStateAction<Conversation | null>>;
    setMobilePanel: (panel: 'list' | 'chat' | 'details') => void;
    showDetails: boolean;
    setShowDetails: (show: boolean) => void;
}

const VoiceTab: React.FC<VoiceTabProps> = ({
    agents,
    teamMembers,
    hasAssignPermission,
    mobilePanel,
    selectedChat,
    setSelectedChat,
    setMobilePanel,
    showDetails,
    setShowDetails,
}) => {
    const { showToast } = useToast();
    // Plan-CSAT/inbox parity (2026-05-15) — pull theme so the
    // transcript renderer can match dark/light modes the same way
    // ChatsTab does.
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const [chats, setChats] = useState<Conversation[]>([]);
    const [loadingChats, setLoadingChats] = useState(false);
    const [selectedAgentId, setSelectedAgentId] = useState<string>('');
    const [selectedChatIds, setSelectedChatIds] = useState<Set<string>>(new Set());
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [assigning, setAssigning] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [addingComment, setAddingComment] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);

    const filteredChats = chats.filter(c => {
        if (!dateFrom && !dateTo) return true;
        // Same NaN hole as ChatsTab: a conversation with no messages has no
        // `last_message_at`, so fall back to `created_at`/`started_at` and
        // exclude the row outright when nothing parses.
        const ts = serverIsoToMillis(c.last_message_at)
            ?? serverIsoToMillis(c.created_at)
            ?? serverIsoToMillis(c.started_at);
        if (ts === null) return false;
        if (dateFrom && ts < new Date(dateFrom).getTime()) return false;
        if (dateTo && ts > new Date(dateTo + 'T23:59:59').getTime()) return false;
        return true;
    });

    const allFilteredSelected = filteredChats.length > 0 && filteredChats.every(c => selectedChatIds.has(c.session_id));
    const someFilteredSelected = filteredChats.some(c => selectedChatIds.has(c.session_id));

    useEffect(() => {
        fetchChats();
    }, [selectedAgentId]);

    useEffect(() => {
        if (selectedChat) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [selectedChat?.messages]);

    const fetchChats = async (from?: string, to?: string) => {
        setLoadingChats(true);
        try {
            const res = await chatsAPI.list(
                selectedAgentId || undefined,
                from ?? (dateFrom || undefined),
                to ?? (dateTo || undefined),
                'voice',
            );
            setChats(res.data);
            setSelectedChatIds(new Set());

            if (selectedChat) {
                const updated = res.data.find(c => c.session_id === selectedChat.session_id);
                if (updated) setSelectedChat(updated);
            }
        } catch (err) {
            showToast('Failed to load voice sessions', 'error');
        } finally {
            setLoadingChats(false);
        }
    };

    const toggleSelectAll = () => {
        if (allFilteredSelected) {
            setSelectedChatIds(new Set());
        } else {
            setSelectedChatIds(new Set(filteredChats.map(c => c.session_id)));
        }
    };

    const toggleSelectChat = (sessionId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedChatIds(prev => {
            const next = new Set(prev);
            next.has(sessionId) ? next.delete(sessionId) : next.add(sessionId);
            return next;
        });
    };

    const handleAssign = async (userId: string) => {
        if (!selectedChat) return;
        setAssigning(true);
        try {
            await chatsAPI.assign(selectedChat.session_id, userId);
            showToast('Session assigned successfully', 'success');
            const member = teamMembers.find(m => m.id === userId);
            setSelectedChat(prev => prev ? { ...prev, assigned_to: userId, assigned_to_name: member?.full_name } : null);
            fetchChats();
        } catch (err) {
            showToast('Failed to assign session', 'error');
        } finally {
            setAssigning(false);
        }
    };

    const handleAddComment = async () => {
        if (!selectedChat || !commentText.trim()) return;
        setAddingComment(true);
        try {
            const res = await chatsAPI.addComment(selectedChat.session_id, commentText);
            const newComment = res.data as ConversationComment;
            setCommentText('');
            setSelectedChat(prev => prev ? {
                ...prev,
                comments: [...(prev.comments ?? []), newComment],
            } : null);
            showToast('Comment added', 'success');
        } catch {
            showToast('Failed to add comment', 'error');
        } finally {
            setAddingComment(false);
        }
    };

    const exportChats = (format: 'csv' | 'json', onlySelected = false) => {
        const source = onlySelected
            ? filteredChats.filter(c => selectedChatIds.has(c.session_id))
            : filteredChats;
        if (source.length === 0) return;

        let content = '';
        let mimeType = '';
        let ext = '';

        if (format === 'json') {
            content = JSON.stringify(source, null, 2);
            mimeType = 'application/json';
            ext = 'json';
        } else {
            const esc = (v: any) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
            const headers = ['Session ID', 'Visitor Name', 'Status', 'Total Messages', 'Created At', 'Last Message At', 'Agent ID', 'Assigned To', 'CSAT Score'];
            const rows = source.map(c => [
                c.session_id,
                c.visitor_name ?? '',
                c.status,
                c.total_messages ?? c.messages.length,
                c.created_at ?? '',
                c.last_message_at ?? '',
                c.agent_id,
                c.assigned_to_name ?? 'Unassigned',
                c.csat_score ?? '',
            ]);
            content = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
            mimeType = 'text/csv';
            ext = 'csv';
        }

        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `voice_sessions_${new Date().toISOString().split('T')[0]}.${ext}`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleSelectChat = (chat: Conversation) => {
        setSelectedChat(chat);
        setMobilePanel('chat');
    };

    const handleBackToList = () => {
        setSelectedChat(null);
        setShowDetails(false);
        setMobilePanel('list');
    };

    const handleToggleDetails = () => {
        const next = !showDetails;
        setShowDetails(next);
        if (next) setMobilePanel('details');
        else setMobilePanel('chat');
    };

    const navigate = useNavigate();

    const handleNavigateToTicket = () => {
        if (!selectedChat?.ticket_id) return;
        navigate(`/tickets?ticket=${selectedChat.ticket_id}`);
    };

    return (
        <div className="flex-1 flex overflow-hidden md:p-4 lg:p-6 md:gap-0">
            <div className="flex-1 flex overflow-hidden md:rounded-2xl md:shadow-sm md:border md:border-gray-200 md:dark:border-gray-800 bg-white dark:bg-gray-900">

                {/* ── Session List Panel ── */}
                <div className={`
                    flex flex-col flex-shrink-0
                    border-r border-gray-200 dark:border-gray-800
                    bg-white dark:bg-gray-900
                    transition-all duration-300
                    ${mobilePanel === 'list' ? 'flex' : 'hidden'}
                    md:flex
                    ${selectedChat ? 'md:w-64 lg:w-72' : 'w-full md:w-full'}
                `}>
                    <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex-shrink-0 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <button
                                    onClick={toggleSelectAll}
                                    className="flex-shrink-0 p-0.5 text-gray-400 hover:text-primary transition-colors"
                                    title={allFilteredSelected ? 'Deselect all' : 'Select all'}
                                >
                                    {allFilteredSelected
                                        ? <CheckSquare className="w-4 h-4 text-primary" />
                                        : someFilteredSelected
                                            ? <CheckSquare className="w-4 h-4 text-primary/50" />
                                            : <Square className="w-4 h-4" />}
                                </button>
                                <h2 className="font-semibold text-gray-900 dark:text-white text-sm truncate">Voice Sessions</h2>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => exportChats('csv')} className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" title="Export all CSV">
                                    <Download className="w-4 h-4" />
                                </button>
                                <button onClick={() => exportChats('json')} className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors font-medium text-xs" title="Export all JSON">
                                    JSON
                                </button>
                            </div>
                        </div>

                        <select
                            className="w-full text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            value={selectedAgentId}
                            onChange={(e) => {
                                setSelectedAgentId(e.target.value);
                                setSelectedChatIds(new Set());
                            }}
                        >
                            <option value="">All Agents</option>
                            {agents.map(a => (
                                <option key={a.id} value={a.id}>{a.config.agent_name}</option>
                            ))}
                        </select>

                        <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={e => { setDateFrom(e.target.value); setSelectedChatIds(new Set()); }}
                                className="flex-1 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary min-w-0"
                                title="From date"
                            />
                            <span className="text-xs text-gray-400">–</span>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={e => { setDateTo(e.target.value); setSelectedChatIds(new Set()); }}
                                className="flex-1 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary min-w-0"
                                title="To date"
                            />
                            {(dateFrom || dateTo) && (
                                <button
                                    onClick={() => { setDateFrom(''); setDateTo(''); setSelectedChatIds(new Set()); }}
                                    className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                    title="Clear date filter"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>

                        {someFilteredSelected && (
                            <div className="flex items-center justify-between bg-primary/5 dark:bg-primary/10 rounded-lg px-2 py-1.5 border border-primary/20">
                                <span className="text-xs font-medium text-primary">
                                    {selectedChatIds.size} selected
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => exportChats('csv', true)}
                                        className="text-xs px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary hover:text-primary transition-colors flex items-center gap-1"
                                    >
                                        <Download className="w-3 h-3" /> CSV
                                    </button>
                                    <button
                                        onClick={() => exportChats('json', true)}
                                        className="text-xs px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary hover:text-primary transition-colors flex items-center gap-1"
                                    >
                                        <Download className="w-3 h-3" /> JSON
                                    </button>
                                    <button
                                        onClick={() => setSelectedChatIds(new Set())}
                                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                        title="Clear selection"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {loadingChats ? (
                            <div className="flex justify-center p-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                            </div>
                        ) : filteredChats.length === 0 ? (
                            <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                                {chats.length > 0 ? 'No sessions match the current filters.' : 'No voice sessions found.'}
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                {filteredChats.map(chat => {
                                    const isChecked = selectedChatIds.has(chat.session_id);
                                    return (
                                        <div
                                            key={chat.session_id}
                                            onClick={() => handleSelectChat(chat)}
                                            className={`group relative p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${selectedChat?.session_id === chat.session_id ? 'bg-primary/5 dark:bg-primary/10 border-l-2 border-primary' : ''
                                                } ${isChecked ? 'bg-primary/5 dark:bg-primary/10' : ''}`}
                                        >
                                            <div className="flex items-start gap-2">
                                                <button
                                                    onClick={(e) => toggleSelectChat(chat.session_id, e)}
                                                    className={`flex-shrink-0 mt-0.5 transition-opacity ${isChecked || someFilteredSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                                                    title={isChecked ? 'Deselect' : 'Select'}
                                                >
                                                    {isChecked
                                                        ? <CheckSquare className="w-4 h-4 text-primary" />
                                                        : <Square className="w-4 h-4 text-gray-400" />}
                                                </button>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between items-start mb-1">
                                                        <div className="flex items-center gap-1.5 min-w-0">
                                                            <Mic className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                                                            <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                                                {chat.visitor_name || chat.session_id.substring(0, 12)}
                                                            </span>
                                                        </div>
                                                        <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                                                            {formatServerDate(chat.last_message_at ?? chat.created_at ?? chat.started_at)}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-1.5">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className={`text-xs px-2 py-0.5 rounded-full ${chat.status === 'active'
                                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                                                }`}>
                                                                {chat.status}
                                                            </span>
                                                            {chat.csat_score != null && (
                                                                <span className="text-xs text-amber-500">{'★'.repeat(chat.csat_score)}</span>
                                                            )}
                                                        </div>
                                                        {chat.assigned_to_name && (
                                                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                                                <UserIcon className="w-3 h-3" />
                                                                {chat.assigned_to_name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Transcript Panel ── */}
                {selectedChat && (
                    <div className={`
                        flex-1 flex flex-col min-w-0
                        border-r border-gray-200 dark:border-gray-800
                        bg-white dark:bg-gray-900
                        ${mobilePanel === 'chat' ? 'flex' : 'hidden'}
                        md:flex
                        ${showDetails ? 'md:flex' : ''}
                    `}>
                        <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/50 shadow-sm z-10 flex-shrink-0">
                            <div className="flex items-center gap-2 md:gap-3 min-w-0">
                                <button
                                    onClick={handleBackToList}
                                    className="md:hidden p-1.5 -ml-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
                                    title="Back to list"
                                >
                                    <X className="w-4 h-4 rotate-180" />
                                </button>
                                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
                                    <Mic className="w-4 h-4 md:w-5 md:h-5 text-violet-600 dark:text-violet-400" />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm md:text-base truncate">
                                        {selectedChat.visitor_name || selectedChat.session_id.substring(0, 12)}
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${selectedChat.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                                        {selectedChat.status}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                                {hasAssignPermission && (
                                    <select
                                        className="hidden sm:block text-xs md:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg px-2 md:px-3 py-1 md:py-1.5 focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 max-w-[120px] md:max-w-none"
                                        value={selectedChat.assigned_to || ""}
                                        onChange={(e) => handleAssign(e.target.value)}
                                        disabled={assigning}
                                    >
                                        <option value="" disabled>Assign to...</option>
                                        {teamMembers.map(m => (
                                            <option key={m.id} value={m.id}>{m.full_name}</option>
                                        ))}
                                    </select>
                                )}
                                <button
                                    onClick={handleToggleDetails}
                                    className={`p-1.5 md:p-2 rounded-lg transition-colors ${showDetails ? 'bg-primary/10 text-primary' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'}`}
                                    title="Session Details"
                                >
                                    <Info className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                                <button
                                    onClick={handleBackToList}
                                    className="hidden md:flex p-1.5 md:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                                    title="Close"
                                >
                                    <X className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Transcript */}
                        <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-4 bg-gray-50 dark:bg-[#0a0a0a]">
                            {selectedChat.messages.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-600 gap-2">
                                    <Mic className="w-10 h-10 opacity-30" />
                                    <p className="text-sm">No transcript available</p>
                                </div>
                            ) : (
                                selectedChat.messages.filter(msg => msg.role !== 'tool').map((msg, idx) => (
                                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[85%] md:max-w-[80%] min-w-0 [overflow-wrap:anywhere] rounded-2xl px-3 md:px-4 py-2 md:py-2.5 shadow-sm ${msg.role === 'user'
                                            ? 'bg-violet-600 text-white rounded-br-none'
                                            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-100 dark:border-gray-700 rounded-bl-none'
                                            }`}>
                                            {/* Inbox parity with ChatsTab (2026-05-15) — route
                                                message bodies through `AgentMessageContent` so
                                                `[HELP_CENTER_CARD:...]` blocks render as inline
                                                article cards instead of raw text. User messages
                                                pass through too: they never emit HELP_CENTER_CARD
                                                blocks in practice, but routing them through the
                                                same renderer keeps the two tabs consistent. */}
                                            <AgentMessageContent
                                                content={msg.content}
                                                role={msg.role === 'user' ? 'user' : 'agent'}
                                                isDark={isDark}
                                            />
                                            <span className={`text-[10px] mt-1 block ${msg.role === 'user' ? 'text-violet-200' : 'text-gray-400 dark:text-gray-500'}`}>
                                                {formatServerTime(msg.timestamp, [], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Read-only footer note */}
                        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0">
                            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                                Voice transcript — read only. Use the details panel to add internal comments.
                            </p>
                        </div>
                    </div>
                )}

                {/* ── Details Panel ── */}
                {selectedChat && showDetails && (
                    <div className={`
                        flex-shrink-0 overflow-hidden
                        bg-gray-50 dark:bg-gray-900
                        border-l border-gray-200 dark:border-gray-800
                        ${mobilePanel === 'details' ? 'flex flex-col w-full' : 'hidden'}
                        lg:flex lg:flex-col lg:w-72
                        md:flex md:flex-col md:w-64
                        p-4 md:p-5
                    `}>
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setMobilePanel('chat')}
                                    className="md:hidden p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg"
                                    title="Back to transcript"
                                >
                                    <X className="w-4 h-4 rotate-180" />
                                </button>
                                <h3 className="font-semibold text-gray-900 dark:text-white">Details</h3>
                            </div>
                            <button onClick={() => { setShowDetails(false); setMobilePanel('chat'); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto space-y-5">
                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Session Info</p>
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm space-y-3">
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">{selectedChat.status}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Started</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                            {formatServerDateTime(selectedChat.created_at || selectedChat.started_at || selectedChat.last_message_at)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Transcript Messages</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedChat.messages.length}</p>
                                    </div>
                                </div>
                            </div>

                            {/* CSAT Rating */}
                            {selectedChat.csat_score != null && (
                                <div>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Customer Satisfaction</p>
                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm space-y-2">
                                        <div className="flex items-center gap-1">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <span key={star} className={`text-lg ${star <= selectedChat.csat_score! ? 'text-amber-400' : 'text-gray-200 dark:text-gray-600'}`}>★</span>
                                            ))}
                                            <span className="ml-1 text-sm font-semibold text-gray-900 dark:text-white">{selectedChat.csat_score}/5</span>
                                        </div>
                                        {selectedChat.csat_feedback && (
                                            <p className="text-xs text-gray-600 dark:text-gray-400 italic">"{selectedChat.csat_feedback}"</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Assignment</p>
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                                    {selectedChat.assigned_to_name ? (
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 flex items-center justify-center font-bold text-xs">
                                                {selectedChat.assigned_to_name.charAt(0)}
                                            </div>
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedChat.assigned_to_name}</span>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">Unassigned</p>
                                    )}
                                </div>
                            </div>

                            {hasAssignPermission && (
                                <div>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Reassign</p>
                                    <select
                                        className="sm:hidden w-full text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50"
                                        value={selectedChat.assigned_to || ""}
                                        onChange={(e) => handleAssign(e.target.value)}
                                        disabled={assigning}
                                    >
                                        <option value="" disabled>Assign to...</option>
                                        {teamMembers.map(m => (
                                            <option key={m.id} value={m.id}>{m.full_name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {selectedChat.ticket_id && (
                                <div>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Linked Ticket</p>
                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                                        <button
                                            onClick={handleNavigateToTicket}
                                            className="flex items-center space-x-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors w-full"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            <span>View Ticket #{selectedChat.ticket_id.split('-')[1] || selectedChat.ticket_id}</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">System Data</p>
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm space-y-3">
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Session ID</p>
                                        <p className="text-xs font-mono text-gray-900 dark:text-white break-all">{selectedChat.session_id}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Agent ID</p>
                                        <p className="text-xs font-mono text-gray-900 dark:text-white break-all">{selectedChat.agent_id}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Internal Comments — matches EmailsTab pattern */}
                            <div className="border-t border-gray-200 dark:border-gray-800 pt-5 mt-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex-shrink-0">Internal Comments</p>

                                <div className="space-y-3 mb-4 pr-1">
                                    {(!selectedChat.comments || selectedChat.comments.length === 0) ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">No comments yet.</p>
                                    ) : (
                                        selectedChat.comments.map((c: ConversationComment) => (
                                            <div key={c.comment_id} className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                                                <div className="flex justify-between items-start mb-1">
                                                    <span className="font-medium text-xs text-gray-900 dark:text-white">{c.author_name}</span>
                                                    <span className="text-[10px] text-gray-400">{formatServerDateTime(c.created_at, [], { dateStyle: 'short', timeStyle: 'short' })}</span>
                                                </div>
                                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{c.text}</p>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <div className="flex-shrink-0 relative">
                                    <textarea
                                        value={commentText}
                                        onChange={e => setCommentText(e.target.value)}
                                        placeholder="Add an internal note..."
                                        className="w-full text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 pb-10 focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none h-24 shadow-sm"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleAddComment();
                                            }
                                        }}
                                    />
                                    <button
                                        onClick={handleAddComment}
                                        disabled={!commentText.trim() || addingComment}
                                        className="absolute bottom-2 right-2 p-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                        title="Add comment"
                                    >
                                        {addingComment ? <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default VoiceTab;
